// src/hooks/useCustomVariants.ts
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext.tsx";

const API_URL = process.env.REACT_APP_API_URL || "";

export interface CustomVariant {
  id: string;
  name: string;
  description?: string;
  boardSize: number;
  whitePieces?: any[];
  blackPieces?: any[];
  specialRules?: any;
  rowsOccupied?: number;
  playersPerSide?: number;
  movesPerTurn?: number;
  checkmatePiece?: string;
  createdAt?: string;
}

const STORAGE_KEY = "expandedChess_customVariants";

export const useCustomVariants = () => {
  const [customVariants, setCustomVariants] = useState<CustomVariant[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  // Fetch custom variants from database API
  const fetchVariantsFromAPI = useCallback(async () => {
    console.log("🔍 fetchVariantsFromAPI called, user:", user?.userId);

    if (!user?.userId) {
      console.log("⚠️ No user ID available, skipping API fetch");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const url = `${API_URL}/api/custom-variants/${user.userId}`;
      console.log("🔍 Fetching custom variants from:", url);

      const response = await fetch(url);
      console.log("🔍 Response status:", response.status);

      if (response.ok) {
        const data = await response.json();
        console.log("🔍 Raw API response:", data);
        const variants = data.variants || [];

        // Transform API response to match our interface
        const transformedVariants: CustomVariant[] = variants.map((v: any) => ({
          id: v.id,
          name: v.name,
          description: v.description,
          boardSize: v.board_size,
          whitePieces: typeof v.white_pieces === 'string' ? JSON.parse(v.white_pieces) : v.white_pieces,
          blackPieces: typeof v.black_pieces === 'string' ? JSON.parse(v.black_pieces) : v.black_pieces,
          specialRules: typeof v.special_rules === 'string' ? JSON.parse(v.special_rules) : v.special_rules,
          rowsOccupied: v.rows_occupied,
          playersPerSide: v.players_per_side,
          movesPerTurn: v.moves_per_turn,
          checkmatePiece: v.checkmate_piece,
          createdAt: v.created_at,
        }));

        console.log("🔍 Transformed variants:", transformedVariants);
        setCustomVariants(transformedVariants);
        console.log(`✅ Loaded ${transformedVariants.length} custom variants from database`);

        // Also update localStorage as backup
        saveToStorage(transformedVariants);
      } else {
        console.warn("⚠️ Failed to fetch variants from API, status:", response.status);
        const errorText = await response.text();
        console.warn("⚠️ Error response:", errorText);
        loadFromStorage();
      }
    } catch (error) {
      console.error("❌ Error fetching custom variants from API:", error);
      // Fall back to localStorage
      loadFromStorage();
    } finally {
      setIsLoading(false);
    }
  }, [user?.userId]);

  // Load from localStorage (fallback)
  const loadFromStorage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const variants = JSON.parse(stored);
        if (Array.isArray(variants)) {
          setCustomVariants(variants);
          console.log(`✅ Loaded ${variants.length} custom variants from localStorage`);
        }
      }
    } catch (error) {
      console.error("❌ Failed to load custom variants from localStorage:", error);
      setCustomVariants([]);
    }
  };

  // Load custom variants on mount and when user changes
  useEffect(() => {
    if (user?.userId) {
      fetchVariantsFromAPI();
    } else {
      loadFromStorage();
      setIsLoading(false);
    }
  }, [user?.userId, fetchVariantsFromAPI]);

  /**
   * Save variants to localStorage
   */
  const saveToStorage = (variants: CustomVariant[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(variants));
      console.log(`💾 Saved ${variants.length} custom variants to localStorage`);
    } catch (error) {
      console.error("❌ Failed to save custom variants:", error);
      setError("Failed to save custom variants");
    }
  };

  /**
   * Refresh variants from database
   */
  const refreshVariants = () => {
    if (user?.userId) {
      fetchVariantsFromAPI();
    }
  };

  /**
   * Add a new custom variant
   */
  const addVariant = (variant: CustomVariant) => {
    const newVariants = [...customVariants, variant];
    setCustomVariants(newVariants);
    saveToStorage(newVariants);
    console.log(`➕ Added custom variant: ${variant.name}`);
  };

  /**
   * Update an existing custom variant
   */
  const updateVariant = (variantId: string, updates: Partial<CustomVariant>) => {
    const newVariants = customVariants.map((v) =>
      v.id === variantId ? { ...v, ...updates } : v
    );
    setCustomVariants(newVariants);
    saveToStorage(newVariants);
    console.log(`✏️ Updated custom variant: ${variantId}`);
  };

  /**
   * Delete a custom variant
   * @param variantId - The ID of the variant to delete
   * @param confirmed - If true, skip confirmation (caller handles confirmation UI)
   */
  const deleteVariant = (variantId: string, confirmed: boolean = false) => {
    if (!confirmed) {
      // Return false to indicate the calling component should show a confirmation modal
      // Then call again with confirmed=true if the user confirms
      return false;
    }

    const newVariants = customVariants.filter((v) => v.id !== variantId);
    setCustomVariants(newVariants);
    saveToStorage(newVariants);
    console.log(`🗑️ Deleted custom variant: ${variantId}`);
    return true;
  };

  /**
   * Get a specific custom variant by ID
   */
  const getVariant = (variantId: string): CustomVariant | undefined => {
    return customVariants.find((v) => v.id === variantId);
  };

  /**
   * Check if a variant ID exists
   */
  const variantExists = (variantId: string): boolean => {
    return customVariants.some((v) => v.id === variantId);
  };

  /**
   * Clear all custom variants
   * @param confirmed - If true, skip confirmation (caller handles confirmation UI)
   */
  const clearAllVariants = (confirmed: boolean = false) => {
    if (!confirmed) {
      // Return false to indicate the calling component should show a confirmation modal
      // Then call again with confirmed=true if the user confirms
      return false;
    }

    setCustomVariants([]);
    saveToStorage([]);
    console.log("🗑️ Cleared all custom variants");
    return true;
  };

  /**
   * Export custom variants as JSON
   */
  const exportVariants = (): string => {
    return JSON.stringify(customVariants, null, 2);
  };

  /**
   * Import custom variants from JSON
   */
  const importVariants = (jsonString: string): boolean => {
    try {
      const imported = JSON.parse(jsonString);
      if (!Array.isArray(imported)) {
        throw new Error("Invalid format: not an array");
      }

      // Validate each variant has required fields
      const valid = imported.every(
        (v: any) =>
          typeof v.id === "string" &&
          typeof v.name === "string" &&
          typeof v.boardSize === "number"
      );

      if (!valid) {
        throw new Error("Invalid format: missing required fields");
      }

      setCustomVariants(imported);
      saveToStorage(imported);
      console.log(`📥 Imported ${imported.length} custom variants`);
      return true;
    } catch (error) {
      console.error("❌ Failed to import custom variants:", error);
      setError("Failed to import custom variants");
      return false;
    }
  };

  return {
    // State
    customVariants,
    isLoading,
    error,

    // Actions
    addVariant,
    updateVariant,
    deleteVariant,
    getVariant,
    variantExists,
    clearAllVariants,
    exportVariants,
    importVariants,
    refreshVariants,
  };
};
